<template>
	<section v-if="instance" class="v2r">

		<!-- ─── Breadcrumb ─── -->
		<a class="v2r__breadcrumb" @click="modalClose">
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
			{{ $t('campaign.back_btn_label') | capitalize }}
		</a>

		<!-- ─── HEADER CARD ─── -->
		<div class="v2r__head-card">
			<div class="v2r__head-top">
				<div>
					<div class="v2r__head-eyebrow">
						<span>{{ instance.is_request ? 'Request' : 'Campaign' }}</span>
						<span class="v2r__id-badge">#{{ instance.id }}</span>
						<span>·</span>
						<span>{{ $t("campaign.status_change_" + getStatus()) }}</span>
					</div>
					<h1 class="v2r__head-title">{{ instance.name }}</h1>
					<p class="v2r__head-sub" v-html="$t('campaign.request_review_subtitle', {
						company: instance.owner_instance ? instance.owner_instance.name : '—',
						manager: instance.created_by ? instance.created_by.username : '—',
					})" />
				</div>
				<button class="v2r__btn v2r__btn--close" @click="modalClose">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
					{{ $t('close') | capitalize }}
				</button>
			</div>

			<!-- Audits per SSP agency -->
			<div class="v2r__audits">
				<div
					v-for="audit in instance.status"
					:key="audit.id"
					class="v2r__audit-row"
				>
					<div
						class="v2r__audit-icon"
						:class="auditIconClass(audit)"
					>
						<!-- Approved -->
						<svg v-if="auditState(audit) === 'green'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
						<!-- Pre-approved -->
						<svg v-else-if="auditState(audit) === 'blue'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
						<!-- Pending -->
						<svg v-else-if="auditState(audit) === 'yellow'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
						<!-- Rejected -->
						<svg v-else-if="auditState(audit) === 'red'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
						<!-- Paused -->
						<svg v-else-if="auditState(audit) === 'purple'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
						<!-- Default / other -->
						<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
					</div>

					<div class="v2r__audit-meta">
						<div class="v2r__audit-agency">{{ audit.ssp_agency_name }}</div>
						<div class="v2r__audit-state">
							<span>{{ $t("campaign.status_change_" + getStatus()) }}</span>
							<span class="v2r__pill" :class="'v2r__pill--' + auditState(audit)">{{ statusPillLabel(audit) }}</span>
						</div>
					</div>

					<div class="v2r__audit-actions">
						<!-- DSP campaign actions -->
						<template v-if="isDSP">
							<button
								v-if="audit.status === statusList.STATUS_PAUSED"
								class="v2r__btn v2r__btn--approve"
								:disabled="isPlayDisabled"
								@click="play(audit)"
							>
								<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
								{{ $t('campaign.play') }}
							</button>
							<button
								v-if="audit.status === statusList.STATUS_PLAYING"
								class="v2r__btn v2r__btn--preapprove"
								@click="pause(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
								{{ $t('campaign.pause') }}
							</button>
							<button
								v-if="audit.status !== statusList.STATUS_ARCHIVED"
								class="v2r__btn v2r__btn--outline"
								@click="archive(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
								{{ $t('campaign.archive') }}
							</button>
						</template>

						<!-- SSP campaign actions (already-approved campaign) -->
						<template v-else-if="isSSPAdminOrApprover && !instance.is_request">
							<button
								v-if="audit.status !== statusList.STATUS_PLAYING"
								class="v2r__btn v2r__btn--approve"
								:disabled="isPlayDisabled"
								@click="play(audit)"
							>
								<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
								{{ $t('campaign.play') }}
							</button>
							<button
								v-if="audit.status !== statusList.STATUS_PAUSED"
								class="v2r__btn v2r__btn--preapprove"
								@click="pause(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
								{{ $t('campaign.pause') }}
							</button>
							<button
								v-if="audit.status !== statusList.STATUS_AWAITING_APPROVAL"
								class="v2r__btn v2r__btn--outline-yellow"
								@click="setWaitingApproval(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
								{{ $t('campaign.status_change_1_action') }}
							</button>
						</template>

						<!-- SSP request approval -->
						<template v-else-if="isSSPAdminOrApprover && instance.is_request">
							<button
								v-if="audit.status !== statusList.STATUS_APPROVED"
								class="v2r__btn v2r__btn--approve"
								:disabled="isPlayDisabled"
								@click="approve(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
								{{ $t('campaign.approve') }}
							</button>
							<button
								v-if="audit.status !== statusList.STATUS_PRE_APPROVED"
								class="v2r__btn v2r__btn--preapprove"
								@click="preApprove(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>
								{{ $t('campaign.status_change_2_action') }}
							</button>
							<button
								v-if="audit.status !== statusList.STATUS_REJECTED"
								class="v2r__btn v2r__btn--reject"
								@click="openRejectModal(audit)"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
								{{ $t('campaign.reject') }}
							</button>
						</template>
					</div>
				</div>
			</div>

			<!-- Insufficient balance alert -->
			<div v-if="isPlayDisabled" class="v2r__alert">
				<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
				<div>
					<b>{{ $t('campaign.approval_disabled_title') || 'Approval is temporarily disabled.' }}</b>
					{{ isDSP ? $t('campaign.insufficient_balance_dsp_view') : $t('campaign.insufficient_balance_ssp_view') }}
				</div>
			</div>
		</div>

		<!-- ─── TABS ─── -->
		<nice-tabs
			v-if="instance"
			class="v2r__tabs"
		>
			<tab-details
				:resource="resource"
				:instance="instance"
				:data-ni_tab_name="$t('campaign.details_tab') | capitalize"
				data-ni_tab_slug="details-tab"
				@update="refreshInstance"
			/>
			<tab-order
				:resource="resource"
				:instance="instance"
				:data-ni_tab_name="$t('campaign.order_tab') | capitalize"
				data-ni_tab_slug="order-tab"
				@update="refreshInstance"
			/>
			<tab-creative
				v-if="stats"
				:resource="resource"
				:instance="instance"
				:stats="stats"
				:data-ni_tab_name="$t('campaign.creative_tab') | capitalize"
				data-ni_tab_slug="creative-tab"
				@update="refreshInstance"
				@pause:campaign="pause(instance.status[0])"
			/>
			<tab-status
				v-if="stats"
				:resource="resource"
				:instance="instance"
				:campaign-stats="stats"
				:data-ni_tab_name="$t('campaign.status_tab') | capitalize"
				data-ni_tab_slug="status-tab"
				@update="refreshInstance"
			/>
			<tab-history
				v-if="!isDSP"
				:instance="instance"
				:status="instance.status[0]"
				:data-ni_tab_name="$t('campaign.history_tab') | capitalize"
				data-ni_tab_slug="history-tab"
				@update="refreshInstance"
			/>
		</nice-tabs>

		<!-- Pre-flight approve warnings -->
		<ApproveWarningsModal
			v-if="showApproveWarningsModal"
			:approve-warnings="approveWarnings"
			@close="showApproveWarningsModal = false"
		/>

		<!-- Reject with reason -->
		<RejectWithReasonModal
			v-if="showRejectModal"
			@close="showRejectModal = false"
			@confirm="onRejectConfirm"
		/>

	</section>
</template>


<script>
import OriginalReview from '@/pages/request/review/index.vue';
import ApproveWarningsModal from './approve-warnings-modal.vue';
import RejectWithReasonModal from './reject-with-reason-modal.vue';
import TabDetails from './tab-details.vue';
import TabOrder from './tab-order.vue';
import TabCreative from './tab-creative.vue';
import TabStatus from './tab-status.vue';
import TabHistory from './tab-history.vue';

// status pill labels mapping
const PILL_LABELS = {
	green: 'Approved',
	blue: 'Pre-approved',
	yellow: 'Pending Audit',
	red: 'Rejected',
	purple: 'Paused',
	navy: 'Finished',
	gray: 'Archived',
};

export default {
	name: 'V2PageRequestReview',

	extends: OriginalReview,

	components: {
		...OriginalReview.components,
		ApproveWarningsModal,
		RejectWithReasonModal,
		// Override V1 tabs with V2 versions
		TabDetails,
		TabOrder,
		TabCreative,
		TabStatus,
		TabHistory,
	},

	data() {
		return {
			...OriginalReview.data.call(this),
			showRejectModal: false,
			rejectingAudit: null,
		};
	},

	methods: {
		auditState(audit) {
			// Map audit.status code to color class
			const s = this.statusList;
			if (audit.status === s.STATUS_APPROVED) return 'green';
			if (audit.status === s.STATUS_PRE_APPROVED) return 'blue';
			if (audit.status === s.STATUS_AWAITING_APPROVAL) return 'yellow';
			if (audit.status === s.STATUS_REJECTED) return 'red';
			if (audit.status === s.STATUS_PAUSED) return 'purple';
			if (audit.status === s.STATUS_FINISHED) return 'navy';
			if (audit.status === s.STATUS_ARCHIVED) return 'gray';
			if (audit.status === s.STATUS_DRAFT) return 'gray';
			return 'yellow';
		},

		auditIconClass(audit) {
			return 'v2r__audit-icon--' + this.auditState(audit);
		},

		statusPillLabel(audit) {
			const key = 'campaign.status_change_' + audit.status;
			const translated = this.$t(key);
			if (translated && translated !== key) return translated;
			return PILL_LABELS[this.auditState(audit)] || 'Pending';
		},

		openRejectModal(audit) {
			this.rejectingAudit = audit;
			this.showRejectModal = true;
		},

		async onRejectConfirm({ reason }) {
			this.showRejectModal = false;
			if (!this.rejectingAudit) return;
			// Delegate to inherited reject; backend currently doesn't accept reason
			// but we capture it for future API extension via emit
			this.$emit('audit-reject-with-reason', {
				audit: this.rejectingAudit,
				reason,
			});
			await this.reject(this.rejectingAudit);
			this.rejectingAudit = null;
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$card: $v2-card-bg
$bg: $v2-bg
$green: $v2-green
$orange: $v2-orange
$red: $v2-red
$blue: $v2-blue
$font: $v2-font
$mono: 'JetBrains Mono', 'Menlo', monospace

.v2r
	max-width: 1180px
	margin: 0 auto
	display: flex
	flex-direction: column
	gap: 16px
	font-family: $font
	font-size: 13px
	color: $text
	padding-bottom: 60px

// ─── Breadcrumb ───
.v2r__breadcrumb
	display: inline-flex
	align-items: center
	gap: 6px
	font-size: 12px
	font-weight: 500
	color: $muted
	cursor: pointer
	width: fit-content

	&:hover
		color: $text
		text-decoration: none

	svg
		width: 14px
		height: 14px

// ─── Header card ───
.v2r__head-card
	background: $card
	border: 1px solid rgba($border, 0.6)
	border-radius: 16px
	box-shadow: 0 1px 3px rgba(0,0,0,0.04)
	overflow: hidden

.v2r__head-top
	padding: 22px 26px
	border-bottom: 1px solid rgba($border, 0.5)
	display: flex
	align-items: flex-start
	justify-content: space-between
	gap: 16px
	flex-wrap: wrap

.v2r__head-eyebrow
	font-size: 11px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.12em
	color: $muted
	display: flex
	align-items: center
	gap: 8px
	margin-bottom: 10px

.v2r__id-badge
	font-family: $mono
	background: rgba($bg, 0.8)
	padding: 2px 8px
	border-radius: 6px
	color: $text

.v2r__head-title
	font-size: 24px
	font-weight: 700
	letter-spacing: -0.02em
	margin: 0

.v2r__head-sub
	margin-top: 6px
	font-size: 13px
	color: $muted

	::v-deep b
		color: $text
		font-weight: 600

// ─── Audits ───
.v2r__audits
	padding: 16px 18px
	display: flex
	flex-direction: column
	gap: 10px

.v2r__audit-row
	display: flex
	align-items: center
	gap: 14px
	padding: 14px 16px
	background: rgba($bg, 0.5)
	border: 1px solid rgba($border, 0.5)
	border-radius: 12px

.v2r__audit-icon
	width: 36px
	height: 36px
	border-radius: 10px
	flex-shrink: 0
	display: flex
	align-items: center
	justify-content: center

	svg
		width: 18px
		height: 18px

	&--green
		background: rgba($green, 0.12)
		color: $green
	&--yellow
		background: rgba($orange, 0.14)
		color: darken($orange, 8%)
	&--blue
		background: rgba($blue, 0.12)
		color: $blue
	&--red
		background: rgba($red, 0.12)
		color: $red
	&--purple
		background: rgba($purple, 0.12)
		color: $purple
	&--gray
		background: rgba($bg, 0.8)
		color: $muted
	&--navy
		background: rgba(#1f2a44, 0.12)
		color: #1f2a44

.v2r__audit-meta
	flex: 1
	min-width: 0

.v2r__audit-agency
	font-size: 13px
	font-weight: 700
	color: $text

.v2r__audit-state
	font-size: 11px
	color: $muted
	margin-top: 3px
	display: inline-flex
	align-items: center
	gap: 6px

.v2r__pill
	display: inline-flex
	align-items: center
	padding: 2px 8px
	border-radius: 9999px
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.04em

	&--green
		background: rgba($green, 0.12)
		color: $green
	&--yellow
		background: rgba($orange, 0.16)
		color: darken($orange, 8%)
	&--blue
		background: rgba($blue, 0.12)
		color: $blue
	&--red
		background: rgba($red, 0.12)
		color: $red
	&--gray
		background: rgba($bg, 0.8)
		color: $muted
	&--purple
		background: rgba($purple, 0.12)
		color: $purple
	&--navy
		background: rgba(#1f2a44, 0.12)
		color: #1f2a44

.v2r__audit-actions
	display: flex
	gap: 8px
	flex-shrink: 0

// ─── Buttons ───
.v2r__btn
	display: inline-flex
	align-items: center
	justify-content: center
	gap: 6px
	height: 36px
	padding: 0 14px
	border-radius: 10px
	font-size: 12px
	font-weight: 600
	font-family: $font
	border: 1px solid transparent
	background: $card
	color: $text
	cursor: pointer
	transition: all 0.15s

	svg
		width: 14px
		height: 14px

	&:disabled
		opacity: 0.5
		cursor: not-allowed

	&--approve
		background: $green
		color: white

		&:hover:not(:disabled)
			background: darken($green, 6%)

	&--preapprove
		border-color: $blue
		color: $blue

		&:hover:not(:disabled)
			background: rgba($blue, 0.08)

	&--reject
		border-color: $red
		color: $red

		&:hover:not(:disabled)
			background: rgba($red, 0.08)

	&--outline
		border-color: $border
		color: $text

		&:hover:not(:disabled)
			background: rgba($bg, 0.5)

	&--outline-yellow
		border-color: $orange
		color: darken($orange, 8%)

		&:hover:not(:disabled)
			background: rgba($orange, 0.08)

	&--close
		border-color: $border
		color: $muted

		&:hover:not(:disabled)
			background: rgba($bg, 0.5)
			color: $text

// ─── Alert ───
.v2r__alert
	display: flex
	align-items: flex-start
	gap: 10px
	padding: 12px 16px
	margin: 0 18px 18px
	background: rgba($red, 0.06)
	border: 1px solid rgba($red, 0.18)
	border-radius: 10px
	font-size: 12px
	color: $red

	svg
		width: 16px
		height: 16px
		flex-shrink: 0
		margin-top: 1px

	b
		font-weight: 700

// ─── Tabs ───
.v2r__tabs
	::v-deep
		.ni_tabs-header
			display: flex
			gap: 4px
			padding: 4px
			background: rgba($bg, 0.7)
			border: 1px solid rgba($border, 0.6)
			border-radius: 12px
			overflow-x: auto

		.ni_tabs-header__item
			display: inline-flex
			align-items: center
			gap: 6px
			padding: 9px 14px
			border-radius: 8px
			font-size: 12px
			font-weight: 600
			color: $muted
			white-space: nowrap
			cursor: pointer
			transition: all 0.15s
			border: none
			background: transparent

			&:hover
				color: $text

			&.active
				background: $card
				color: $text
				box-shadow: 0 1px 3px rgba(0,0,0,0.06)

		.ni_tabs-content
			padding-top: 16px

		// Reset V1 styles inside tab content
		.layout-center805,
		.layout_block-full_width
			max-width: 100% !important
			margin: 0 !important
			padding: 0 !important
</style>
